// Shared types + display helpers for the Driver Interview Scoring module.

export type ApplicationRow = {
  application_id: string
  full_name: string | null
  primary_email: string | null
  primary_phone: string | null
  current_status: string
  score: number | null
  submitted_at: string | null
  last_email_template_key?: string | null
  last_email_sent_at?: string | null
}

export type SelectedApplication = {
  application_id: string
  full_name: string | null
  current_status: string
  score: number | null
  primary_email: string | null
  primary_phone: string | null
  whatsapp_phone: string | null
  province: string | null
  town: string | null
  was_blocked: boolean | null
  block_reason: string | null
  gate_k_has_uber_profile: boolean | null
  contrib_l_currently_driving_uber: boolean | null
  gate_m_rating_valid: boolean | null
  gate_n_pro_level_valid: boolean | null
  gate_q_province_gauteng: boolean | null
  gate_r_town_nonblank: boolean | null
  uber_rating_bucket: string | null
  uber_pro_level: string | null
  document_links: unknown
  raw_payload?: unknown
  is_driver?: boolean | null
  last_email_template_key?: string | null
  last_email_sent_at?: string | null
  prdp_verdict?: string | null
  prdp_expiry?: string | null
  prdp_categories?: string | null
  uber_verdict?: string | null
  uber_evidence?: string | null
  screening_read_failure?: string | null
  prdp_document_type?: string | null
  prdp_valid_until?: string | null
  prdp_id_check?: string | null
  prdp_card_received_at?: string | null
  [key: string]: unknown
}

export type InterviewRow = {
  id: string
  round: number
  manager_id: string | null
  scheduled_at: string | null
  booked_at: string | null
  completed_at: string | null
  result: number | null
  notes: string | null
  canceled_at: string | null
  cancellation_reason: string | null
}

export type Manager = {
  id: string
  first_name: string | null
  last_name: string | null
}

export type WorklistRow = {
  interview_id: string
  application_id: string
  round: number
  interview_at: string | null
  current_status: string
  person_id: string | null
  full_name: string | null
  primary_email: string | null
  primary_phone: string | null
  whatsapp_phone: string | null
  score: number | null
  likely_duplicate?: boolean
  duplicate_survivor_application_id?: string | null
  duplicate_survivor_name?: string | null
  duplicate_survivor_status?: string | null
  duplicate_match_basis?: string | null
  prdp_verdict?: string | null
  prdp_expiry?: string | null
  prdp_categories?: string | null
  uber_verdict?: string | null
  uber_evidence?: string | null
  screening_read_failure?: string | null
  prdp_document_type?: string | null
  prdp_valid_until?: string | null
  prdp_id_check?: string | null
  prdp_card_received_at?: string | null
  // Migration 0274. Who still has to score and where the reference calls stand.
  // Optional: absent on a response from before 0274.
  progress?: InterviewProgress | null
}

export type ScorerProgress = {
  role: 'manager' | 'second'
  name: string | null
  short_name: string
  status: 'submitted' | 'draft' | 'not_started'
  // Migration 0275. False for an Interview 2 seat the row names nobody for,
  // labelled "Second scorer not assigned". Absent before 0275.
  assigned?: boolean
  // Written by interview_progress_v1, e.g. "Thabang scoring complete".
  label: string
}

export type InterviewProgress = {
  interview_id: string
  round: number
  scorers: ScorerProgress[]
  unassigned: boolean
  scoring_complete: boolean
  // Migration 6800. The drive at the end of an Interview 2, present only where
  // it is required. Never carries the result: once submitted the label reads
  // "Drive recorded" for everybody, so the card cannot unseal a score.
  drive?: { status: 'not_started' | 'draft' | 'submitted'; label: string } | null
  // Migration 6801. "Luyanda sat out" when the second scorer stepped out.
  sat_out?: string | null
  references: {
    due: boolean
    required: number
    completed: number
    open: number
    unreachable: number
    missing: number
    // Migration 2801. Whether every reference the rule requires is in. Under
    // the conditional rule that can be one of one, so completed >= required
    // is not the test. Absent before 2801.
    complete?: boolean
    caller: string
    label: string
  }
  summary: string
}

export type DriverIncident = {
  id: string
  incident_date: string
  category: string
  severity: string
  description: string | null
  created_at: string
}

// Aggregate performance for a driver over a date range. Numeric fields can
// arrive as strings from the DB, so coerce with Number() in the UI.
export type DriverPerformance = {
  shift_count: number | string
  total_earnings: number | string
  avg_earnings: number | string | null
  take_home: number | string
  petrol_litres: number | string
  petrol_cost: number | string
  petrol_savings: number | string
  kilometres: number | string
  km_per_litre: number | string | null
  extras: number | string
  on_time_shifts: number | string
  on_time_pct: number | string | null
  target_loss: number | string
}

export type DriverLastShift = {
  shift_date: string
  earnings: number | string
  take_home: number | string
  petrol_litres: number | string
  petrol_cost: number | string
  petrol_savings: number | string
  kilometres: number | string
  km_per_litre: number | string | null
  extras: number | string
  on_time: boolean
  target_loss: number | string
}

export type DriverShiftRow = {
  shift_id: string
  shift_date: string
  shift_window: string | null
  status: string | null
  earnings: number | string
  take_home: number | string
  petrol_litres: number | string
  petrol_savings: number | string
  kilometres: number | string
  km_per_litre: number | string | null
  extras: number | string
  on_time: boolean
  target_loss: number | string
}

export type DriverRepair = {
  id: string
  raised_at: string | null
  amount_zar: number | string
  outstanding_zar: number | string
  status: string | null
  fault_attribution: string | null
  reason: string | null
  recovered_at: string | null
}

export type DriverPayslip = {
  month: string
  shift_count: number | string
  gross_earnings: number | string
  extras: number | string
  shortfalls: number | string
  repairs: number | string
  net_pay: number | string
}

export type DriverOperationalIncident = {
  id: string
  occurred_at: string | null
  incident_type: string | null
  severity: string | null
  status: string | null
  drivable: boolean
  cost_zar: number | string
  title: string | null
  description: string | null
}

// Format a Rand amount ZA-style: thousands separated by a space, 2 decimals
// with a dot. e.g. 6377.92 -> "R6 377.92". A missing amount (null, blank, not a
// number) is the no-value marker, never "R0.00": a missing figure shows as
// missing (CLAUDE.md rule 6, audit B16). Built by hand rather than with
// toLocaleString('en-ZA'), which uses a comma for the decimal.
export function formatZar(v: number | string | null | undefined): string {
  if (v == null || v === '') return '\u2014'
  const n = Number(v)
  if (!Number.isFinite(n)) return '\u2014'
  const [whole, cents] = Math.abs(n).toFixed(2).split('.')
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  return 'R' + (n < 0 ? '-' : '') + grouped + '.' + cents
}

// Format a plain number (km, km/l, litres) with a fixed number of decimals.
export function formatNum(
  v: number | string | null | undefined,
  dp = 1,
): string {
  const n = v == null || v === '' ? 0 : Number(v)
  const safe = Number.isFinite(n) ? n : 0
  return safe.toLocaleString('en-ZA', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  }).replace(/\u00a0|\u202f/g, ' ')
}

export type DuplicateCandidate = {
  application_id: string
  person_id: string | null
  full_name: string | null
  current_status: string
  stage_rank: number
  submitted_at: string | null
  match_basis: string
  match_score: number
}

const MATCH_BASIS_LABELS: Record<string, string> = {
  id: 'same ID number',
  email: 'same email',
  phone: 'same phone',
  name: 'same name',
}

export function matchBasisLabel(basis: string | null | undefined): string {
  if (!basis) return ''
  return MATCH_BASIS_LABELS[basis] ?? ''
}

// Every pipeline stage with a plain-language label, in workflow order.
export type StatusOption = { code: string; label: string }

export const PIPELINE_STATUS_OPTIONS: StatusOption[] = [
  { code: 'submitted', label: 'Applied' },
  { code: 'rejected_score', label: 'Rejected on score' },
  { code: 'interview_1_pending', label: 'Interview 1 pending' },
  { code: 'interview_1_booked', label: 'Interview 1 booked' },
  { code: 'interview_1_failed', label: 'Interview 1 failed' },
  { code: 'interview_1_no_show', label: 'Interview 1 no-show' },
  { code: 'interview_2_pending', label: 'Interview 2 pending' },
  { code: 'interview_2_booked', label: 'Interview 2 booked' },
  { code: 'interview_2_failed', label: 'Interview 2 failed' },
  { code: 'offer_pending', label: 'Offer pending' },
  { code: 'offer_accepted', label: 'Offer accepted' },
  { code: 'onboarding_form_pending', label: 'Onboarding form pending' },
  { code: 'contract_pending', label: 'Contract pending' },
  { code: 'contract_signed', label: 'Contract signed' },
  { code: 'induction_pending', label: 'Induction pending' },
  { code: 'induction_acknowledged', label: 'Induction acknowledged' },
  { code: 'ready_to_activate', label: 'Ready to activate' },
  { code: 'active', label: 'Active' },
  { code: 'withdrawn', label: 'Withdrawn' },
]

export const INTERVIEW_STATUSES = [
  'interview_1_pending',
  'interview_1_booked',
  'interview_2_pending',
  'interview_2_booked',
] as const

const STATUS_LABELS: Record<string, string> = {
  submitted: 'Applied',
  rejected_score: 'Rejected on score',
  interview_1_pending: 'Interview 1 pending',
  interview_1_booked: 'Interview 1 booked',
  interview_1_failed: 'Interview 1 failed',
  interview_1_no_show: 'Interview 1 no-show',
  interview_2_pending: 'Interview 2 pending',
  interview_2_booked: 'Interview 2 booked',
  interview_2_failed: 'Interview 2 failed',
  offer_pending: 'Offer pending',
  offer_accepted: 'Offer accepted',
  onboarding_form_pending: 'Onboarding form pending',
  contract_pending: 'Contract pending',
  contract_signed: 'Contract signed',
  induction_pending: 'Induction pending',
  induction_acknowledged: 'Induction acknowledged',
  ready_to_activate: 'Ready to activate',
  active: 'Active',
  offboarding: 'Offboarding',
  compliance: 'Compliance',
  suspended: 'Suspended',
  dismissed: 'Dismissed',
  resigned: 'Resigned',
  withdrawn: 'Withdrawn',
}

export function statusLabel(status: string | null | undefined): string {
  if (!status) return 'Unknown'
  if (STATUS_LABELS[status]) return STATUS_LABELS[status]!
  return status
    .split('_')
    .map((w) => (w.length ? w[0]!.toUpperCase() + w.slice(1) : w))
    .join(' ')
}

// Friendly labels for the platform email template keys surfaced by
// v_person_last_email. Unknown keys are humanised as a fallback.
const EMAIL_TEMPLATE_LABELS: Record<string, string> = {
  driver_application_interview_1_invite: 'Interview 1 invite',
  driver_application_interview_1_passed: 'Passed interview 1, final interview invite',
  driver_application_interview_1_failed: 'Interview 1 result (not successful)',
  driver_application_interview_2_failed: 'Final interview result (not successful)',
  driver_application_offer: 'Employment offer',
  driver_application_onboarding_welcome: 'Onboarding welcome',
  driver_application_induction_pack: 'Induction pack',
  driver_application_academy_invite: 'Academy invite',
  driver_application_rejected_low_rating: 'Rejected: low rating',
  driver_application_rejected_not_gauteng: 'Rejected: not Gauteng',
  driver_application_rejected_uber_inactive: 'Rejected: Uber inactive',
  driver_application_rejected_blocked: 'Rejected: blocked',
  driver_application_rejected_form_data: 'Rejected: form data',
  driver_birthday_greeting: 'Birthday greeting',
  driver_application_interview_invite_v2: 'Interview invite',
  driver_application_rejected_no_prdp: 'Rejected: no valid PrDP',
  driver_application_rejected_uber_blocked: 'Rejected: Uber deactivated',
  driver_application_prdp_unreadable: 'Asked for a clear PrDP photo',
  driver_application_academy_enrolled: 'Added to the Academy',
  academy_apply_invite: 'Academy: invited to apply',
}

export function emailTemplateLabel(key: string | null | undefined): string {
  if (!key) return ''
  if (EMAIL_TEMPLATE_LABELS[key]) return EMAIL_TEMPLATE_LABELS[key]!
  const stripped = key.replace(/^driver_application_/, '').replace(/^driver_/, '')
  const spaced = stripped.replace(/_/g, ' ').trim()
  if (!spaced) return ''
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

export type ContractStatus = {
  envelope_id: string
  status: string
  template_name: string | null
  sent_at: string | null
  signed_at: string | null
  voided_at: string | null
}

// Plain-language label for the latest contract envelope state.
export function contractStatusLabel(s: ContractStatus | null): string {
  if (!s) return 'No contract sent yet'
  switch (s.status) {
    case 'signed':
      return 'Signed'
    case 'sent':
      return 'Awaiting signature'
    case 'voided':
      return 'Voided'
    case 'declined':
      return 'Declined'
    default:
      return s.status
  }
}

export type StatusTone = 'brand' | 'amber' | 'green' | 'red' | 'neutral'

export function statusTone(status: string | null | undefined): StatusTone {
  if (!status) return 'neutral'
  if (status === 'compliance') return 'amber'
  if (status === 'offboarding') return 'amber'
  if (status === 'suspended') return 'red'
  if (status === 'dismissed') return 'red'
  // Leaving by choice is not a mark against anyone, so it is not coloured as one.
  if (status === 'resigned') return 'neutral'
  if (status.includes('reject') || status.includes('fail') || status.includes('no_show') || status.includes('block') || status === 'withdrawn')
    return 'red'
  if (status === 'active' || status.includes('accepted') || status.includes('signed') || status.includes('acknowledged') || status === 'ready_to_activate')
    return 'green'
  if (status.endsWith('_booked')) return 'brand'
  if (status.endsWith('_pending') || status === 'submitted') return 'amber'
  return 'neutral'
}

export type Outcome = {
  label: string
  tone: 'green' | 'red' | 'amber' | 'neutral'
}

export function interviewOutcome(iv: InterviewRow): Outcome {
  if (iv.canceled_at) return { label: 'Canceled', tone: 'neutral' }
  if (iv.round === 1) {
    switch (iv.result) {
      case 2:
        return { label: 'Passed', tone: 'green' }
      case 1:
        return { label: 'Failed', tone: 'red' }
      case 0:
        return { label: 'No-show', tone: 'amber' }
      default:
        return { label: 'Pending', tone: 'neutral' }
    }
  }
  // Round 2 (and any later round)
  switch (iv.result) {
    case 1:
      return { label: 'Offer', tone: 'green' }
    case 0:
      return { label: 'Failed', tone: 'red' }
    default:
      return { label: 'Pending', tone: 'neutral' }
  }
}

export function roundLabel(round: number): string {
  if (round === 1) return 'Round 1 phone interview'
  if (round === 2) return 'Round 2 in-person interview'
  return `Round ${round} interview`
}

export type DueFlag = 'today' | 'overdue' | null

// Flags an interview time as due today or overdue relative to now.
export function dueFlag(value: string | null | undefined): DueFlag {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return null
  const now = new Date()
  if (d.getTime() < now.getTime()) return 'overdue'
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  return sameDay ? 'today' : null
}

export function isActionable(iv: InterviewRow): boolean {
  return iv.completed_at == null && iv.canceled_at == null
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })
}

export type DocLink = { label: string; url: string }

export function normalizeDocLinks(raw: unknown): DocLink[] {
  if (!Array.isArray(raw)) return []
  const out: DocLink[] = []
  raw.forEach((item, i) => {
    if (typeof item === 'string') {
      out.push({ label: labelFromUrl(item, i), url: item })
      return
    }
    if (item && typeof item === 'object') {
      const obj = item as Record<string, unknown>
      const url =
        pickString(obj, ['url', 'href', 'link', 'signed_url', 'signedUrl', 'path']) ?? ''
      if (!url) return
      const label =
        pickString(obj, ['label', 'name', 'title', 'type', 'document_type', 'kind']) ??
        labelFromUrl(url, i)
      out.push({ label, url })
    }
  })
  return out
}

function pickString(obj: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = obj[k]
    if (typeof v === 'string' && v.trim()) return v
  }
  return undefined
}

function labelFromUrl(url: string, i: number): string {
  try {
    const clean = url.split('?')[0] ?? url
    const parts = clean.split('/').filter(Boolean)
    const last = parts[parts.length - 1]
    if (last) return decodeURIComponent(last)
  } catch {
    /* ignore */
  }
  return `Document ${i + 1}`
}

export type ApplicantAnswer = { fieldId: string; question: string; value: string }

// Every answer the applicant gave on the Driver Employment Form (or, for an
// older record, whatever form the raw payload holds), matched back to its own
// question by field id. File uploads are excluded on purpose: those screenshots
// belong in the Documents panel, which already turns them into a real, titled,
// ownable copy instead of a link into somebody else's Typeform account.
//
// Defensive by necessity, not by habit: this reads a payload that came from a
// webhook we do not control, for applications going back to before some of
// these fields existed. A malformed or missing payload returns an empty list,
// never a crash.
export function applicantAnswers(rawPayload: unknown): ApplicantAnswer[] {
  const formResponse = formResponseOf(rawPayload)
  if (!formResponse) return []

  const fields = Array.isArray(formResponse.definition_fields)
    ? formResponse.definition_fields
    : []
  const titleById = new Map<string, string>()
  for (const field of fields) {
    if (!field || typeof field !== 'object') continue
    const f = field as Record<string, unknown>
    const id = f.id
    const title = f.title
    if (typeof id === 'string' && typeof title === 'string' && title.trim()) {
      titleById.set(id, title.trim())
    }
  }

  const answers = Array.isArray(formResponse.answers) ? formResponse.answers : []
  const out: ApplicantAnswer[] = []
  answers.forEach((raw, i) => {
    if (!raw || typeof raw !== 'object') return
    const a = raw as Record<string, unknown>
    const type = typeof a.type === 'string' ? a.type : null
    // Screenshots and other uploads are shown, and can be pulled in as a real
    // copy, by the Documents panel. They do not belong in a list of answers.
    if (!type || type === 'file_url') return

    const fieldId = pickFieldId(a)
    const question = (fieldId && titleById.get(fieldId)) || `Question ${i + 1}`
    const value = answerValueText(a, type)
    if (value == null) return
    out.push({ fieldId: fieldId ?? `q${i}`, question, value })
  })
  return out
}

function formResponseOf(
  rawPayload: unknown,
): { definition_fields: unknown; answers: unknown } | null {
  if (!rawPayload || typeof rawPayload !== 'object') return null
  const obj = rawPayload as Record<string, unknown>
  const nested = obj.form_response
  const fr =
    nested && typeof nested === 'object'
      ? (nested as Record<string, unknown>)
      : Array.isArray(obj.answers)
        ? obj
        : null
  if (!fr) return null
  const definition = fr.definition
  const fieldsValue =
    definition && typeof definition === 'object'
      ? (definition as Record<string, unknown>).fields
      : undefined
  return { definition_fields: fieldsValue, answers: fr.answers }
}

function pickFieldId(a: Record<string, unknown>): string | null {
  const field = a.field
  if (field && typeof field === 'object') {
    const id = (field as Record<string, unknown>).id
    if (typeof id === 'string') return id
  }
  return null
}

// Typeform stores most answers under a key that matches their own type name
// (text, email, phone_number, number, date, boolean), so the fallback below
// covers all of them at once. "choice" and "choices" carry an object rather
// than a scalar and are unwrapped explicitly.
function answerValueText(a: Record<string, unknown>, type: string): string | null {
  if (type === 'choice') {
    const choice = a.choice
    if (choice && typeof choice === 'object') {
      const label = (choice as Record<string, unknown>).label
      if (typeof label === 'string' && label.trim()) return label.trim()
    }
    return null
  }
  if (type === 'choices') {
    const choices = a.choices
    if (choices && typeof choices === 'object') {
      const labels = (choices as Record<string, unknown>).labels
      if (Array.isArray(labels)) {
        const joined = labels.filter((l): l is string => typeof l === 'string').join(', ')
        return joined || null
      }
    }
    return null
  }
  const raw = a[type]
  if (typeof raw === 'string') return raw.trim() || null
  if (typeof raw === 'number') return String(raw)
  if (typeof raw === 'boolean') return raw ? 'Yes' : 'No'
  return null
}

export function initials(name: string | null | undefined): string {
  if (!name) return '—'
  const parts = name.trim().split(/\s+/)
  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? parts[parts.length - 1]![0] ?? '' : ''
  return (first + last).toUpperCase() || '—'
}

// The calendar day an interview falls on in Johannesburg, as yyyy-mm-dd. The
// browser's own timezone is not used: a laptop left on UTC would put a 01:00
// interview on the day before.
export function sastDayKey(value: string | Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Johannesburg',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(value))
}

export type InterviewDay<T> = { key: string; label: string; isToday: boolean; items: T[] }

// Groups the Interviews tab by day, in the order the rows arrive (the handler
// sorts by time). Today is labelled Today and tomorrow Tomorrow, because that is
// how anybody planning the day says it; every other day gets its weekday.
export function groupByInterviewDay<T extends { interview_at: string | null }>(
  rows: T[],
  now: Date = new Date(),
): InterviewDay<T>[] {
  const today = sastDayKey(now)
  const tomorrow = sastDayKey(new Date(now.getTime() + 24 * 60 * 60 * 1000))
  const days: InterviewDay<T>[] = []
  for (const row of rows) {
    if (!row.interview_at) continue
    const key = sastDayKey(row.interview_at)
    let day = days.find((d) => d.key === key)
    if (!day) {
      const date = new Date(row.interview_at).toLocaleDateString('en-ZA', {
        timeZone: 'Africa/Johannesburg',
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      })
      const label = key === today ? `Today, ${date}` : key === tomorrow ? `Tomorrow, ${date}` : date
      day = { key, label, isToday: key === today, items: [] }
      days.push(day)
    }
    day.items.push(row)
  }
  return days
}
